import { useTranslation } from "react-i18next";
import type { BudgetStatusResponse } from "@/api";
import { InlineNotice } from "@/components/InlineNotice";
import { PageSection } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { formatDate } from "@/i18n/format";
import { budgetLevel, formatCost } from "./spend";

const BAR_CLASS = {
  ok: "[&::-moz-progress-bar]:bg-brand-primary [&::-webkit-progress-value]:bg-brand-primary",
  warning: "[&::-moz-progress-bar]:bg-warning [&::-webkit-progress-value]:bg-warning",
  blocked: "[&::-moz-progress-bar]:bg-error [&::-webkit-progress-value]:bg-error",
} as const;

const PERCENT_TONE = { ok: "secondary", warning: "warning", blocked: "error" } as const;

export function BudgetStatus({ budget }: Readonly<{ budget: BudgetStatusResponse }>) {
  const { t } = useTranslation("settings");
  const level = budgetLevel(budget);
  const pct = Math.max(0, Math.min(100, budget.percentUsed));
  const resets = formatDate(budget.resetsAt, { month: "long", day: "numeric", timeZone: "UTC" });
  return (
    <PageSection
      title={t("usage.budget.title")}
      description={t("usage.budget.description", { limit: formatCost(budget.monthlyLimitUsd) })}
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between gap-4">
          <Text as="span" variant="body-sm">
            {t("usage.budget.spent", {
              spent: formatCost(budget.currentSpendUsd),
              limit: formatCost(budget.monthlyLimitUsd),
            })}
          </Text>
          <Text as="span" variant="body-sm" tone={PERCENT_TONE[level]}>
            {t("usage.budget.percent", { percent: Math.round(budget.percentUsed) })}
          </Text>
        </div>
        <progress
          aria-label={t("usage.budget.title")}
          max={100}
          value={Math.round(pct)}
          className={`h-2 w-full appearance-none overflow-hidden rounded-full bg-surface-secondary [&::-moz-progress-bar]:rounded-full [&::-webkit-progress-bar]:bg-surface-secondary [&::-webkit-progress-value]:rounded-full ${BAR_CLASS[level]}`}
        />
        <Text as="span" variant="caption" tone="tertiary">
          {t("usage.budget.resets", { date: resets })}
        </Text>
      </div>
      {level === "blocked" && <InlineNotice tone="error">{t("usage.budget.blocked", { date: resets })}</InlineNotice>}
      {level === "warning" && <InlineNotice tone="warning">{t("usage.budget.warning")}</InlineNotice>}
    </PageSection>
  );
}
